import { WalletButton } from "./WalletButton";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RocketCoin } from "../icons/RocketCoin";
import { GreenNotepad } from "../icons/GreenNotepad";
import { useLocation, useNavigate } from "react-router-dom";
import { useAdmin } from '@/hooks/useAdmin';
import { useAccount } from 'wagmi';
import { Capacitor } from '@capacitor/core';
import { 
  Droplets,
  Crown,
  Lock,
  Link,
  User,
  DollarSign
} from 'lucide-react';
import { ThemeToggle } from "./ThemeToggle";
import { useTheme } from "@/providers/ThemeProvider";
import NetworkSwitch from "./NetworkSwitch";
import { Button } from '@/components/ui/button';
import { SpinningTools } from '../icons/SpinningTools';
import { GradientImage } from "../icons/GradientImage";
import { SafeLink } from "@/components/ui/SafeLink";
import { safeNavigate } from "@/utils/mobile-compatibility";
import { toast } from 'sonner';
import { AuthModal } from "@/components/auth/AuthModal";
import { useUserAuth } from "@/providers/UserAuthProvider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface HeaderProps {
  showWalletConnect?: boolean;
  showNetworkSelector?: boolean;
}

export function Header({ showWalletConnect = true, showNetworkSelector = true }: HeaderProps) {
  const [authOpen, setAuthOpen] = useState(false);
  const [buttonLayout, setButtonLayout] = useState<'Top' | 'Bottom' | 'Left' | 'Right'>('Top');
  const [shimmeringItem, setShimmeringItem] = useState<string | null>(null);
  const [themeRefreshVersion, setThemeRefreshVersion] = useState(0);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const { address } = useAccount();
  const { user, logout } = useUserAuth();
  const { theme: themeMode } = useTheme();
  const isCreatePage = pathname === '/create';
  const isHomePage = pathname === '/';
  const isAccountPage = pathname.startsWith('/account');

  const walletBarRef = useRef<HTMLDivElement>(null);
  const mainHeaderRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (walletBarRef.current) {
        document.documentElement.style.setProperty('--wallet-bar-height', `${walletBarRef.current.offsetHeight}px`);
      }
      if (mainHeaderRef.current) {
        document.documentElement.style.setProperty('--main-header-height', `${mainHeaderRef.current.offsetHeight}px`);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
    // Observe main header size changes (e.g., when labels expand)
    let ro: ResizeObserver | null = null;
    if (window.ResizeObserver && mainHeaderRef.current) {
      ro = new ResizeObserver(() => {
        if (mainHeaderRef.current) {
          document.documentElement.style.setProperty('--main-header-height', `${mainHeaderRef.current.offsetHeight}px`);
        }
      });
      ro.observe(mainHeaderRef.current);
    }

    return () => {
      if (ro && mainHeaderRef.current) ro.disconnect();
    };
  }, []);

  useEffect(() => {
    if (walletBarRef.current) {
      document.documentElement.style.setProperty('--wallet-bar-height', `${walletBarRef.current.offsetHeight}px`);
    }
    if (mainHeaderRef.current) {
      document.documentElement.style.setProperty('--main-header-height', `${mainHeaderRef.current.offsetHeight}px`);
    }
  }, [buttonLayout]);

  // Load button layout from localStorage
  useEffect(() => {
    const savedLayout = localStorage.getItem('buttonLayout') as 'Top' | 'Bottom' | 'Left' | 'Right' | null;
    if (savedLayout) {
      setButtonLayout(savedLayout);
    }

    const handleLayoutChange = (event: CustomEvent) => {
      setButtonLayout(event.detail);
    };

    window.addEventListener('buttonLayoutChanged', handleLayoutChange as EventListener);
    return () => {
      window.removeEventListener('buttonLayoutChanged', handleLayoutChange as EventListener);
    };
  }, []);

  // All available background effects in order
  const backgroundEffects = [
    'off', 'matrix', 'smoke', 'particles', 'waves', 'aurora', 'orbs', 'bubbles',
    'plasma', 'plasmasmoke', 'lightning', 'constellation', 'cybergrid', 'digital', 'dnahelix',
    'fireworks', 'flowfield', 'geometric', 'hexagongrid', 'neuralnetwork',
    'pulserings', 'rain', 'snow', 'starfield', 'vortex'
  ];

  // All available preset themes in order
  const presetThemes = [
    {
      name: 'MdbApp Default',
      colors: {
        primary: '#0a74ff',
        secondary: '#10b981',
        background: '#000000',
        cardBg: '#1a1a1a',
        text: '#ffffff',
        border: '#333333',
        headerAccent: '#0a74ff'
      },
      effect: 'starfield'
    },
    {
      name: 'Baller',
      colors: {
        primary: '#FAD000',
        secondary: '#FFBB00',
        background: '#000000',
        cardBg: '#1a1200',
        text: '#CC9600',
        border: '#7A5A00',
        headerAccent: '#FAD000'
      },
      effect: 'plasma'
    },
    {
      name: 'Matrix',
      colors: {
        primary: '#00ff00',
        secondary: '#1E711E',
        background: '#000000',
        cardBg: '#001100',
        text: '#009E00',
        border: '#003300',
        headerAccent: '#00ff00'
      },
      effect: 'matrix'
    },
    {
      name: 'King Blue',
      colors: {
        primary: '#0A74FF',
        secondary: '#0A74FF',
        background: '#000000',
        cardBg: '#001133',
        text: '#666666',
        border: '#0A74FF',
        headerAccent: '#0A74FF'
      },
      effect: 'plasma'
    },
    {
      name: 'Blade',
      colors: {
        primary: '#FF0000',
        secondary: '#FF0000',
        background: '#000000',
        cardBg: '#330000',
        text: '#707070',
        border: '#FF0000',
        headerAccent: '#FF0000'
      },
      effect: 'plasma'
    },
    {
      name: 'Don',
      colors: {
        primary: '#6600FF',
        secondary: '#6600FF',
        background: '#000000',
        cardBg: '#140a1f',
        text: '#00FF4C',
        border: '#6600FF',
        headerAccent: '#6600FF'
      },
      effect: 'plasma'
    },
    {
      name: 'Ocean',
      colors: {
        primary: '#06b6d4',
        secondary: '#0891b2',
        background: '#0c1821',
        cardBg: '#164e63',
        text: '#cffafe',
        border: '#0e7490',
        headerAccent: '#06b6d4'
      },
      effect: 'plasma'
    },
    {
      name: 'Sunset',
      colors: {
        primary: '#f97316',
        secondary: '#ea580c',
        background: '#1c1917',
        cardBg: '#292524',
        text: '#fed7aa',
        border: '#78716c',
        headerAccent: '#f97316'
      },
      effect: 'plasma'
    },
    {
      name: 'Smoke',
      colors: {
        primary: '#5e5e5e',
        secondary: '#5e5e5e',
        background: '#0c1821',
        cardBg: '#15232f',
        text: '#eff5f6',
        border: '#5e5e5e',
        headerAccent: '#5e5e5e'
      },
      effect: 'plasmasmoke'
    },
    {
      name: 'CbrPnk',
      colors: {
        primary: '#0084FF',
        secondary: '#BB00FF',
        background: '#000000',
        cardBg: '#1a0a1a',
        text: '#EC5CFF',
        border: '#330033',
        headerAccent: '#0084FF'
      },
      effect: 'matrix'
    },
    {
      name: 'CbrPnkII',
      colors: {
        primary: '#ff0080',
        secondary: '#00ffff',
        background: '#0a0a0a',
        cardBg: '#1a0a1a',
        text: '#ff00ff',
        border: '#330033',
        headerAccent: '#ff0080'
      },
      effect: 'matrix'
    },
    {
      name: 'CbrPnkIII',
      colors: {
        primary: '#0084FF',
        secondary: '#BB00FF',
        background: '#000000',
        cardBg: '#1a0a1a',
        text: '#00FBFF',
        border: '#330033',
        headerAccent: '#0084FF'
      },
      effect: 'matrix'
    },
    {
      name: 'Aqua',
      colors: {
        primary: '#00D1AE',
        secondary: '#00CED1',
        background: '#000000',
        cardBg: '#1a1a1a',
        text: '#737373',
        border: '#00D1AE',
        headerAccent: '#00D1AE'
      },
      effect: 'bubbles'
    },
    {
      name: 'BSCChain',
      colors: {
        primary: '#F3BA2F',
        secondary: '#F3BA2F',
        background: '#000000',
        cardBg: '#1a1400',
        text: '#FFFFFF',
        border: '#F3BA2F',
        headerAccent: '#F3BA2F'
      },
      effect: 'hexagongrid'
    },
    {
      name: 'AVX',
      colors: {
        primary: '#E84142',
        secondary: '#E84142',
        background: '#000000',
        cardBg: '#1a0a0a',
        text: '#FFFFFF',
        border: '#E84142',
        headerAccent: '#E84142'
      },
      effect: 'snow'
    },
    {
      name: 'Abstract',
      colors: {
        primary: '#34DA84',
        secondary: '#34DA84',
        background: '#000000',
        cardBg: '#0f1a14',
        text: '#FFFFFF',
        border: '#34DA84',
        headerAccent: '#34DA84'
      },
      effect: 'neuralnetwork'
    },
    {
      name: 'ETH',
      colors: {
        primary: '#3C3C3D',
        secondary: '#3C3C3D',
        background: '#000000',
        cardBg: '#1a1a1a',
        text: '#FFFFFF',
        border: '#3C3C3D',
        headerAccent: '#3C3C3D'
      },
      effect: 'constellation'
    },
    {
      name: 'Base',
      colors: {
        primary: '#FFFFFF',
        secondary: '#0A74FF',
        background: '#FFFFFF',
        cardBg: '#f8fafc',
        text: '#0A74FF',
        border: '#e2e8f0',
        headerAccent: '#FFFFFF'
      },
      effect: 'geometric'
    },
    {
      name: 'Poly',
      colors: {
        primary: '#7b3fe4',
        secondary: '#7b3fe4',
        background: '#000000',
        cardBg: '#1a0f2e',
        text: '#FFFFFF',
        border: '#7b3fe4',
        headerAccent: '#7b3fe4'
      },
      effect: 'particles'
    },
    {
      name: 'OP',
      colors: {
        primary: '#FF0621',
        secondary: '#FF0621',
        background: '#000000',
        cardBg: '#330000',
        text: '#FFFFFF',
        border: '#FF0621',
        headerAccent: '#FF0621'
      },
      effect: 'neuralnetwork'
    }
  ];

  // Helper function to trigger shimmer effect
  const triggerShimmer = (identifier: string) => {
    setShimmeringItem(identifier);
    setTimeout(() => setShimmeringItem(null), 800);
  };

  // Helper function to convert hex to HSL tuple
  const hexToHslTuple = (hex: string): string => {
    if (!hex) return '0 0% 100%';
    hex = hex.replace('#', '').replace('rgb(', '').replace(')', '').trim();
    
    // Handle rgb format
    if (hex.includes(',')) {
      const [r, g, b] = hex.split(',').map(v => parseInt(v.trim()) / 255);
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h = 0, s = 0, l = (max + min) / 2;
      if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
        }
      }
      return Math.round(h * 360) + ' ' + Math.round(s * 100) + '% ' + Math.round(l * 100) + '%';
    }
    
    // Handle hex format
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }
    return Math.round(h * 360) + ' ' + Math.round(s * 100) + '% ' + Math.round(l * 100) + '%';
  };

  // Function to apply a theme
  const applyTheme = (theme: typeof presetThemes[0], mode?: string) => {
    const { colors, effect } = theme;
    const themeMode = mode || localStorage.getItem('theme') || 'dark';
    
    // Special handling for Base theme in light mode
    // In light mode, the white primary becomes invisible, so use text color (blue) instead
    const isBaseThemeInLightMode = theme.name === 'Base' && themeMode === 'light';
    const effectivePrimary = isBaseThemeInLightMode ? colors.text : colors.headerAccent;
    
    // Apply colors to CSS variables used across the app
    const root = document.documentElement;
    root.style.setProperty('--color-primary', effectivePrimary);
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--color-background', colors.background);
    root.style.setProperty('--color-card-bg', colors.cardBg);
    root.style.setProperty('--color-text', colors.text);
    root.style.setProperty('--text-color', colors.text);
    root.style.setProperty('--color-border', colors.border);
    root.style.setProperty('--color-header-accent', effectivePrimary);

    // Sync Tailwind/shadcn theme variables so focus rings and borders follow the active theme
    const primaryHsl = hexToHslTuple(colors.primary || colors.headerAccent);
    const borderHsl = hexToHslTuple(colors.border || colors.primary || colors.headerAccent);
    root.style.setProperty('--primary', primaryHsl);
    root.style.setProperty('--ring', primaryHsl);
    root.style.setProperty('--border', borderHsl);
    
    // Convert text color to HSL for foreground variables
    const textHsl = hexToHslTuple(colors.text);
    root.style.setProperty('--foreground', textHsl);
    root.style.setProperty('--muted-foreground', textHsl);
    
    // Save theme to localStorage in the same format as SwatchPro
    const themeData = {
      colors: {
        [themeMode]: colors
      },
      timestamp: Date.now()
    };
    localStorage.setItem('userAppCustomTheme', JSON.stringify(themeData));
    
    // Apply background effect
    localStorage.setItem('backgroundEffect', effect);
    window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: effect }));
  };

  // Add shimmer animation keyframes
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes shimmer {
        0% {
          left: -100%;
        }
        100% {
          left: 100%;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Listen for network theme change events
  useEffect(() => {
    const handleNetworkThemeChange = (event: CustomEvent) => {
      const { themeName } = event.detail;
      
      // Find the theme by name
      const theme = presetThemes.find(t => t.name === themeName);
      if (theme) {
        applyTheme(theme);
        console.log(`Applied ${themeName} theme for network change`);
      }
    };

    // Add event listener
    window.addEventListener('applyNetworkTheme', handleNetworkThemeChange as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener('applyNetworkTheme', handleNetworkThemeChange as EventListener);
    };
  }, []);

  // Listen for app theme changes (e.g., from SwatchPro) so header logo updates immediately
  useEffect(() => {
    const handleUserAppThemeChanged = (_event: Event) => {
      // Defer the refresh so SwatchPro can finish updating localStorage.currentThemeName
      setTimeout(() => {
        setThemeRefreshVersion(prev => prev + 1);
      }, 0);
    };

    window.addEventListener('userAppThemeChanged', handleUserAppThemeChanged as EventListener);

    return () => {
      window.removeEventListener('userAppThemeChanged', handleUserAppThemeChanged as EventListener);
    };
  }, []);

  // Listen for theme mode changes (light/dark toggle) and re-apply current theme
  useEffect(() => {
    // When theme mode changes, re-apply the current theme with the new mode
    const currentThemeName = localStorage.getItem('currentThemeName') || 'MdbApp Default';
    const theme = presetThemes.find(t => t.name === currentThemeName);
    if (theme) {
      applyTheme(theme, themeMode);
      console.log(`Re-applied ${currentThemeName} theme for mode: ${themeMode}`);
    }
  }, [themeMode]);

  // Cycle through themes (only on home page)
  const cycleTheme = (e: React.MouseEvent) => {
    if (!isHomePage) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const currentThemeName = localStorage.getItem('currentThemeName') || 'MdbApp Default';
    const currentIndex = presetThemes.findIndex(theme => theme.name === currentThemeName);
    const nextIndex = (currentIndex + 1) % presetThemes.length;
    const nextTheme = presetThemes[nextIndex];
    
    localStorage.setItem('currentThemeName', nextTheme.name);
    applyTheme(nextTheme);
  };

  // Get current theme name for display
  const getCurrentThemeName = () => {
    return localStorage.getItem('currentThemeName') || 'MdbApp Default';
  };

  // Get theme-specific header logo
  const getHeaderLogo = () => {
    const currentThemeName = getCurrentThemeName();
    
    // Use theme-specific logos
    if (currentThemeName === 'Matrix') {
      return '/Dynamic Header Logos/MdbApp.io Matrix Logo.png';
    }

    if (currentThemeName === 'Baller') {
      return '/Dynamic Header Logos/Mdbapp.io Baller Logo.png';
    }

    if (currentThemeName === 'BSCChain') {
      return '/Dynamic Header Logos/Mdbapp.io Baller Logo.png';
    }

    if (currentThemeName === 'AVX' || currentThemeName === 'OP') {
      return '/Dynamic Header Logos/MdbApp.io AVAX_OP Logo.png';
    }

    if (currentThemeName === 'Abstract') {
      return '/Dynamic Header Logos/MdbApp,io Abstract Logo.png';
    }

    if (currentThemeName === 'Poly') {
      return '/Dynamic Header Logos/MdbApp.io Polygon Logo.png';
    }

    if (currentThemeName === 'Base') {
      return '/Dynamic Header Logos/MdbApp.io Base Logo.png';
    }

    if (currentThemeName === 'ETH') {
      return '/Dynamic Header Logos/MdbApp.io ETH logo.png';
    }
    
    if (currentThemeName === 'Don') {
      return '/Dynamic Header Logos/MdbApp.io Don Logo.png';
    }
    
    if (currentThemeName === 'Blade') {
      return '/Dynamic Header Logos/MdbApp.io Blade Logo.png';
    }
    
    if (currentThemeName === 'King Blue') {
      return '/Dynamic Header Logos/MdbApp.io King Blue Logo.png';
    }
    
    if (currentThemeName === 'Aqua') {
      return '/Dynamic Header Logos/MdbApp.io Aqua Logo.png';
    }

    if (currentThemeName === 'Ocean') {
      return '/Dynamic Header Logos/MdbApp.io Ocean Logo.png';
    }

    if (currentThemeName === 'Smoke') {
      return '/Dynamic Header Logos/MdbApp.io Smoke Logo.png';
    }

    if (currentThemeName === 'CbrPnk') {
      return '/Dynamic Header Logos/MdbApp.io CbrPnk Logo.png';
    }

    if (currentThemeName === 'CbrPnkII') {
      return '/Dynamic Header Logos/MdbApp.io CbrPnkII Logo.png';
    }

    if (currentThemeName === 'CbrPnkIII') {
      return '/Dynamic Header Logos/MdbApp.io CbrPnkIII Logo.png';
    }

    if (currentThemeName === 'Sunset') {
      return '/Dynamic Header Logos/MdbApp.io Sunset Logo.png';
    }
    
    return '/Dynamic Header Logos/MdbApp.io Default Logo.png';
  };

  // Handle Earn 50% click
  const handleEarnClick = () => {
    if (isCreatePage) {
      window.location.href = '/account';
    } else {
      safeNavigate(navigate, '/account');
    }
  };

  // Handle share functionality
  const handleShare = async () => {
    const shareData = {
      title: 'MdbApp Token Creator',
      text: 'Create your own token on Ethereum, BSC, or Avalanche with MdbApp Token Creator',
      url: 'https://mdbapp.io'
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        toast.success('Thanks for sharing!');
      } else {
        await navigator.clipboard.writeText(shareData.url);
        toast.success('Link copied to clipboard!');
      }
    } catch (err) {
      console.error('Error sharing:', err);
      toast.error('Unable to share. Please try again.');
    }
  };

  // Handle direct navigation for create page
  const handleNavigation = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Log navigation attempt
    console.log('Header navigation to:', path);
    
    // Use direct window.location for create page to ensure navigation works
    if (isCreatePage) {
      window.location.href = path;
    } else {
      // Use safe navigation for other pages
      safeNavigate(navigate, path);
    }
  };

  return (
    <>
      <style>{`
        @keyframes gradient-move {
          0% { background-position: 0% 0%; }
          100% { background-position: 100% 100%; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes spin-reverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
      `}</style>
      {/* Very top hairline to match header accent (covers safe-area top) */}
      <div
        className="fixed top-0 left-0 right-0 h-[1.4px] z-[102]"
        style={{ backgroundColor: 'var(--color-header-accent, #0A74FF)', boxShadow: '0 0 8px var(--color-header-accent, rgba(10, 116, 255, 0.5))' }}
      />
      {/* Wallet Bar - position changes based on layout */}
      <div
        ref={walletBarRef}
        className={`fixed w-full bg-background/50 backdrop-blur-sm ${
          buttonLayout === 'Top' ? 'z-[100]' : 'top-0 z-[101]'
        }`}
        style={
          buttonLayout === 'Top'
            ? { top: 'calc(env(safe-area-inset-top) + var(--main-header-height))' }
            : { paddingTop: 'env(safe-area-inset-top)' }
        }
      >
        {/* Top Blue Line */}
        <div className="w-full h-[1.4px]" style={{ backgroundColor: 'var(--color-header-accent, #0A74FF)', boxShadow: '0 0 8px var(--color-header-accent, rgba(10, 116, 255, 0.5))' }}></div>
        
        {/* Wallet Button Row */}
        <div className="container mx-auto px-2 sm:px-4 h-12 relative">
          {/* Absolutely centered Wallet Button */}
          {showWalletConnect && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="pointer-events-auto">
                <WalletButton />
              </div>
            </div>
          )}
          <div className="h-full flex items-center justify-between gap-2">
            {/* Left side - Earn 50% and Admin buttons */}
            <div className="flex items-center gap-2">
              {/* Earn 50% Button */}
              <button
                onClick={handleEarnClick}
                className={`inline-flex items-center gap-2 h-9 rounded-lg border bg-transparent transition-all hover:-translate-y-0.5 font-semibold text-sm ${isAdmin ? 'w-9 justify-center' : 'px-3'}`}
                style={{ borderColor: 'var(--color-header-accent, #0A74FF)', color: 'var(--color-header-accent, #0A74FF)' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-header-accent, rgba(10, 116, 255, 0.1))'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                title="Earn 50%"
              >
                <DollarSign className="w-4 h-4 text-[#00FF00] animate-flip-money" />
                {!isAdmin && <span className="hidden sm:inline">Earn 50%</span>}
                {!isAdmin && <span className="sm:hidden">Earn</span>}
              </button>

              {/* Admin Button - Only visible to admins */}
              {isAdmin && (
                <button
                  onClick={() => {
                    if (isCreatePage) {
                      window.location.href = '/admin';
                    } else {
                      safeNavigate(navigate, '/admin');
                    }
                  }}
                  className="inline-flex items-center justify-center h-9 w-9 rounded-md border-2 bg-transparent hover:bg-accent transition-all"
                  style={{ borderColor: 'var(--color-header-accent, #0A74FF)' }}
                  title="Admin Panel"
                >
                  <Crown className="w-4 h-4 text-[var(--color-header-accent,#0A74FF)]" />
                </button>
              )}
            </div>
            {/* Center - Empty to keep structure (Wallet is absolutely centered) */}
            <div className="flex-1 flex items-center justify-center gap-4" />
            {/* Right Side - Network + Auth dropdown */}
            <div className="flex items-center gap-2">
              {showWalletConnect && showNetworkSelector && (
                <div className="flex-none">
                  <NetworkSwitch />
                </div>
              )}
              {!user ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0 hover:bg-accent rounded-md border-2 group relative"
                  style={{ borderColor: 'var(--color-header-accent, #0A74FF)' }}
                  title="Sign in"
                  type="button"
                  onClick={() => setAuthOpen(true)}
                >
                  <User className="w-4 h-4" style={{ color: 'var(--color-header-accent, #0A74FF)' }} />
                </Button>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="h-9 px-2 inline-flex items-center gap-2 rounded-md border-2 hover:bg-accent"
                      style={{ borderColor: 'var(--color-header-accent, #0A74FF)' }}
                      title={user.email || 'Account'}
                    >
                      <img
                        src={user.photoURL || '/Dynamic Header Logos/MdbApp.io Default Logo.png'}
                        alt="avatar"
                        className="w-6 h-6 rounded-full border"
                        onError={(e:any)=>{e.currentTarget.src='/Dynamic Header Logos/MdbApp.io Default Logo.png'}}
                      />
                      <span className="hidden sm:block text-sm max-w-[140px] truncate">{user.displayName || user.email || 'Account'}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div className="flex items-center gap-2">
                        <img 
                          src={user.photoURL || '/Dynamic Header Logos/MdbApp.io Default Logo.png'} 
                          alt="avatar"
                          className="w-7 h-7 rounded-full border"
                          onError={(e:any)=>{e.currentTarget.src='/Dynamic Header Logos/MdbApp.io Default Logo.png'}}
                        />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">{user.displayName || 'Signed in'}</div>
                          <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                        </div>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => {
                      triggerShimmer('account-settings');
                      setTimeout(() => {
                        if (isCreatePage || isAccountPage) {
                          navigate('/account/settings');
                        } else {
                          safeNavigate(navigate, '/account/settings');
                        }
                      }, 200);
                    }} className="justify-center relative overflow-hidden">
                      <span className="relative z-10">Account Settings</span>
                      {shimmeringItem === 'account-settings' && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(10, 116, 255, 0.6), transparent)',
                            animation: 'shimmer 0.8s ease-in-out',
                            pointerEvents: 'none',
                            zIndex: 1
                          }}
                        />
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => {
                      triggerShimmer('referrals');
                      setTimeout(() => {
                        if (isCreatePage || isAccountPage) {
                          navigate('/account/referrals');
                        } else {
                          safeNavigate(navigate, '/account/referrals');
                        }
                      }, 200);
                    }} className="justify-center relative overflow-hidden">
                      <span className="relative z-10">Referrals</span>
                      {shimmeringItem === 'referrals' && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(10, 116, 255, 0.6), transparent)',
                            animation: 'shimmer 0.8s ease-in-out',
                            pointerEvents: 'none',
                            zIndex: 1
                          }}
                        />
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => {
                      triggerShimmer('app-settings');
                      setTimeout(() => {
                        if (isCreatePage || isAccountPage) {
                          navigate('/account/app-settings');
                        } else {
                          safeNavigate(navigate, '/account/app-settings');
                        }
                      }, 200);
                    }} className="justify-center relative overflow-hidden">
                      <span className="relative z-10">App Settings</span>
                      {shimmeringItem === 'app-settings' && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(10, 116, 255, 0.6), transparent)',
                            animation: 'shimmer 0.8s ease-in-out',
                            pointerEvents: 'none',
                            zIndex: 1
                          }}
                        />
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => {
                      triggerShimmer('home');
                      setTimeout(() => {
                        if (isCreatePage) {
                          window.location.href = '/';
                        } else {
                          safeNavigate(navigate, '/');
                        }
                      }, 200);
                    }} className="justify-center relative overflow-hidden">
                      <span className="relative z-10">Home</span>
                      {shimmeringItem === 'home' && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(10, 116, 255, 0.6), transparent)',
                            animation: 'shimmer 0.8s ease-in-out',
                            pointerEvents: 'none',
                            zIndex: 1
                          }}
                        />
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground text-center">Button Layout</DropdownMenuLabel>
                    <div className="flex gap-2 px-2 py-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-8"
                        style={buttonLayout === 'Top' ? {
                          backgroundColor: 'var(--color-header-accent, #0A74FF)',
                          borderColor: 'var(--color-header-accent, #0A74FF)',
                          color: 'var(--color-text, #ffffff)'
                        } : {}}
                        onClick={() => {
                          setButtonLayout('Top');
                          localStorage.setItem('buttonLayout', 'Top');
                          window.dispatchEvent(new CustomEvent('buttonLayoutChanged', { detail: 'Top' }));
                        }}
                      >
                        Top
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-8"
                        style={buttonLayout === 'Bottom' ? {
                          backgroundColor: 'var(--color-header-accent, #0A74FF)',
                          borderColor: 'var(--color-header-accent, #0A74FF)',
                          color: 'var(--color-text, #ffffff)'
                        } : {}}
                        onClick={() => {
                          setButtonLayout('Bottom');
                          localStorage.setItem('buttonLayout', 'Bottom');
                          window.dispatchEvent(new CustomEvent('buttonLayoutChanged', { detail: 'Bottom' }));
                        }}
                      >
                        Bottom
                      </Button>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={(e) => {
                      e.preventDefault();
                      triggerShimmer('themes-cycle');
                      const currentThemeName = localStorage.getItem('currentThemeName') || 'MdbApp Default';
                      const currentIndex = presetThemes.findIndex(theme => theme.name === currentThemeName);
                      const nextIndex = (currentIndex + 1) % presetThemes.length;
                      const nextTheme = presetThemes[nextIndex];
                      localStorage.setItem('currentThemeName', nextTheme.name);
                      applyTheme(nextTheme);
                    }} className="justify-center relative overflow-hidden">
                      <span className="relative z-10">Themes Cycle</span>
                      {shimmeringItem === 'themes-cycle' && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(10, 116, 255, 0.6), transparent)',
                            animation: 'shimmer 0.8s ease-in-out',
                            pointerEvents: 'none',
                            zIndex: 1
                          }}
                        />
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => {
                      e.preventDefault();
                      triggerShimmer('effects-cycle');
                      const currentEffect = localStorage.getItem('backgroundEffect') || 'plasmasmoke';
                      const currentIndex = backgroundEffects.indexOf(currentEffect);
                      const nextIndex = (currentIndex + 1) % backgroundEffects.length;
                      const nextEffect = backgroundEffects[nextIndex];
                      localStorage.setItem('backgroundEffect', nextEffect);
                      window.dispatchEvent(new CustomEvent('backgroundEffectChanged', { detail: nextEffect }));
                    }} className="justify-center relative overflow-hidden">
                      <span className="relative z-10">Effects Cycle</span>
                      {shimmeringItem === 'effects-cycle' && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(10, 116, 255, 0.6), transparent)',
                            animation: 'shimmer 0.8s ease-in-out',
                            pointerEvents: 'none',
                            zIndex: 1
                          }}
                        />
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={async () => { try { await logout(); } catch (e) { console.error(e); } }} className="text-red-600 justify-center">
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>
        <div className="w-full h-[1.4px]" style={{ backgroundColor: 'var(--color-header-accent, #0A74FF)', boxShadow: '0 0 8px var(--color-header-accent, rgba(10, 116, 255, 0.5))' }}></div>
      </div>

      {/* Main Header - Positioned based on buttonLayout */}
      <header ref={mainHeaderRef} className={`fixed bg-background/50 backdrop-blur-sm ${buttonLayout === 'Top' ? 'z-[101]' : 'z-[100]'} transition-all duration-300 ${
        buttonLayout === 'Bottom' 
          ? 'w-full border-t border-[var(--color-header-accent,#0A74FF)]' 
          : buttonLayout === 'Left'
          ? 'left-0 top-[calc(env(safe-area-inset-top)+49px)] bottom-0 w-20 border-r border-[var(--color-header-accent,#0A74FF)] flex-col'
          : buttonLayout === 'Right'
          ? 'right-0 top-[calc(env(safe-area-inset-top)+49px)] bottom-0 w-20 border-l border-[var(--color-header-accent,#0A74FF)] flex-col'
          : 'top-0 w-full'
      }`} style={{
        ...(buttonLayout === 'Top' && { top: 'env(safe-area-inset-top)' }),
        ...(buttonLayout === 'Bottom' && { bottom: 0 })
      }}>
        {/* Top Blue Line - Conditional based on layout */}
        {(buttonLayout === 'Top' || buttonLayout === 'Bottom') && (
          <div className="w-full h-[1.4px]" style={{ backgroundColor: 'var(--color-header-accent, #0A74FF)', boxShadow: '0 0 8px var(--color-header-accent, rgba(10, 116, 255, 0.5))' }}></div>
        )}
        {(buttonLayout === 'Left' || buttonLayout === 'Right') && (
          <div className="h-full w-[1.4px] absolute top-0" style={{ 
            backgroundColor: 'var(--color-header-accent, #0A74FF)',
            boxShadow: '0 0 8px var(--color-header-accent, rgba(10, 116, 255, 0.5))',
            [buttonLayout === 'Left' ? 'right' : 'left']: 0
          }}></div>
        )}
      {/* Main Header Row */}
      <div className={`${
        buttonLayout === 'Left' || buttonLayout === 'Right' 
          ? 'flex flex-col items-center justify-center gap-3 py-4 h-full w-full' 
          : 'container mx-auto px-2 sm:px-4 h-12 lg:h-14'
      }`}>
        <div className={`${
          buttonLayout === 'Left' || buttonLayout === 'Right'
            ? 'flex flex-col items-center gap-3 w-full'
            : 'h-full flex items-center justify-between gap-2'
        }`}>
          {/* Logo - Only show in horizontal layouts */}
          {(buttonLayout === 'Top' || buttonLayout === 'Bottom') && (
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 flex items-center">
              {isHomePage ? (
                <button
                  onClick={cycleTheme}
                  className="inline-flex items-center justify-center h-9 w-9 hover:bg-accent rounded-md transition-colors border-2 group cursor-pointer"
                  style={{ borderColor: 'var(--color-header-accent, #0A74FF)' }}
                  title={`Current Theme: ${getCurrentThemeName()} - Click to cycle`}
                >
                  <img src={getHeaderLogo()} alt="Cycle Theme" className="h-[29px] w-[29px] object-contain" />
                </button>
              ) : isCreatePage ? (
                <a 
                  href="/"
                  className="inline-flex items-center justify-center h-9 w-9 hover:bg-accent rounded-md transition-colors border-2 group"
                  style={{ borderColor: 'var(--color-header-accent, #0A74FF)' }}
                  onClick={handleNavigation('/')}
                  title="Home"
                >
                  <img src={getHeaderLogo()} alt="MdbApp Home" className="h-[29px] w-[29px] object-contain" />
                </a>
              ) : (
                <SafeLink 
                  to="/"
                  className="inline-flex items-center justify-center h-9 w-9 hover:bg-accent rounded-md transition-colors border-2 group"
                  style={{ borderColor: 'var(--color-header-accent, #0A74FF)' }}
                  title="Home"
                >
                  <img src={getHeaderLogo()} alt="MdbApp Home" className="h-[29px] w-[29px] object-contain" />
                </SafeLink>
              )}
            </div>
            {/* Admin button moved to lower row */}
          </div>
          )}

          {/* Main Navigation */}
          <div className={`${
            buttonLayout === 'Left' || buttonLayout === 'Right'
              ? 'flex flex-col items-center gap-2 w-full'
              : 'flex-1 flex items-center justify-center gap-1.5 lg:gap-3'
          }`}>
            <nav className={`${
              buttonLayout === 'Left' || buttonLayout === 'Right'
                ? 'flex flex-col items-center gap-2 w-full'
                : 'flex items-center justify-center gap-1.5 lg:gap-3 max-w-4xl w-full'
            }`}>
              <div className={`${
                buttonLayout === 'Left' || buttonLayout === 'Right'
                  ? 'flex flex-col items-center gap-2'
                  : 'flex items-center justify-center gap-1.5 lg:gap-3'
              }`}>
                <Button asChild variant="ghost" className="h-9 lg:h-9 lg:flex-row lg:gap-2 lg:px-3 lg:w-auto w-9 hover:bg-accent rounded-md border-2 group p-0" style={{ borderColor: 'var(--color-header-accent, #0A74FF)' } as React.CSSProperties}>
                  {isCreatePage ? (
                    <a 
                      href="/create"
                      className="inline-flex flex-col items-center justify-center gap-0.5"
                      title="Create Token"
                      onClick={handleNavigation('/create')}
                    >
                      <RocketCoin className="w-4 h-4" />
                      <span className="hidden lg:inline text-sm">Create</span>
                      <span className="text-[10px] lg:hidden -mt-1">Create</span>
                    </a>
                  ) : (
                    <SafeLink 
                      to="/create"
                      className="inline-flex flex-col items-center justify-center gap-0.5"
                      title="Create Token"
                    >
                      <RocketCoin className="w-4 h-4" />
                      <span className="hidden lg:inline text-sm">Create</span>
                      <span className="text-[10px] lg:hidden -mt-1">Create</span>
                    </SafeLink>
                  )}
                </Button>
                <Button asChild variant="ghost" className="h-9 lg:h-9 lg:flex-row lg:gap-2 lg:px-3 lg:w-auto w-9 hover:bg-accent rounded-md border-2 group p-0" style={{ borderColor: 'var(--color-header-accent, #0A74FF)' } as React.CSSProperties}>
                  {isCreatePage ? (
                    <a
                      href="/tokentools"
                      className="inline-flex flex-col items-center justify-center gap-0.5"
                      title="Token Tools"
                      onClick={handleNavigation('/tokentools')}
                    >
                      <SpinningTools />
                      <span className="hidden lg:inline text-sm">Tools</span>
                      <span className="text-[10px] lg:hidden -mt-1">Tools</span>
                    </a>
                  ) : (
                    <SafeLink
                      to="/tokentools"
                      className="inline-flex flex-col items-center justify-center gap-0.5"
                      title="Token Tools"
                    >
                      <SpinningTools />
                      <span className="hidden lg:inline text-sm">Tools</span>
                      <span className="text-[10px] lg:hidden -mt-1">Tools</span>
                    </SafeLink>
                  )}
                </Button>
                <Button asChild variant="ghost" className="h-9 lg:h-9 lg:flex-row lg:gap-2 lg:px-3 lg:w-auto w-9 hover:bg-accent rounded-md border-2 group p-0" style={{ borderColor: 'var(--color-header-accent, #0A74FF)' } as React.CSSProperties}>
                  {isCreatePage ? (
                    <a
                      href="/smartcontractlocker"
                      className="inline-flex flex-col items-center justify-center gap-0.5"
                      title="Smart Contract Locker"
                      onClick={handleNavigation('/smartcontractlocker')}
                    >
                      <Lock className="w-4 h-4" style={{ color: 'var(--color-header-accent, #0A74FF)' }} />
                      <span className="hidden lg:inline text-sm">Locker</span>
                      <span className="text-[10px] lg:hidden -mt-1">Lock</span>
                    </a>
                  ) : (
                    <SafeLink
                      to="/smartcontractlocker"
                      className="inline-flex flex-col items-center justify-center gap-0.5"
                      title="Smart Contract Locker"
                    >
                      <Lock className="w-4 h-4" style={{ color: 'var(--color-header-accent, #0A74FF)' }} />
                      <span className="hidden lg:inline text-sm">Locker</span>
                      <span className="text-[10px] lg:hidden -mt-1">Lock</span>
                    </SafeLink>
                  )}
                </Button>
                <Button asChild variant="ghost" className="h-9 lg:h-9 lg:flex-row lg:gap-2 lg:px-3 lg:w-auto w-9 hover:bg-accent rounded-md border-2 group p-0" style={{ borderColor: 'var(--color-header-accent, #0A74FF)' } as React.CSSProperties}>
                  {isCreatePage ? (
                    <a
                      href="/mdbmatrixcontractscan"
                      className="inline-flex flex-col items-center justify-center gap-0.5"
                      title="MxScan"
                      onClick={handleNavigation('/mdbmatrixcontractscan')}
                    >
                      <GreenNotepad className="h-4 w-4 text-[var(--color-header-accent,#0A74FF)]" />
                      <span className="hidden lg:inline text-sm">MxScan</span>
                      <span className="text-[10px] lg:hidden -mt-1">Scan</span>
                    </a>
                  ) : (
                    <SafeLink
                      to="/mdbmatrixcontractscan"
                      className="inline-flex flex-col items-center justify-center gap-0.5"
                      title="MxScan"
                    >
                      <GreenNotepad className="h-4 w-4 text-[var(--color-header-accent,#0A74FF)]" />
                      <span className="hidden lg:inline text-sm">MxScan</span>
                      <span className="text-[10px] lg:hidden -mt-1">Scan</span>
                    </SafeLink>
                  )}
                </Button>
                <Button asChild variant="ghost" className="h-9 lg:h-9 lg:flex-row lg:gap-2 lg:px-3 lg:w-auto w-9 hover:bg-accent rounded-md border-2 group p-0" style={{ borderColor: 'var(--color-header-accent, #0A74FF)' } as React.CSSProperties}>
                  {isCreatePage ? (
                    <a
                      href="/tokenlogo"
                      className="inline-flex flex-col items-center justify-center gap-0.5"
                      title="Logo"
                      onClick={handleNavigation('/tokenlogo')}
                    >
                      <GradientImage />
                      <span className="hidden lg:inline text-sm">Logo</span>
                      <span className="text-[10px] lg:hidden -mt-1">Logo</span>
                    </a>
                  ) : (
                    <SafeLink
                      to="/tokenlogo"
                      className="inline-flex flex-col items-center justify-center gap-0.5"
                      title="Logo"
                    >
                      <GradientImage />
                      <span className="hidden lg:inline text-sm">Logo</span>
                      <span className="text-[10px] lg:hidden -mt-1">Logo</span>
                    </SafeLink>
                  )}
                </Button>
                <Button asChild variant="ghost" className="h-9 lg:h-9 lg:flex-row lg:gap-2 lg:px-3 lg:w-auto w-9 hover:bg-accent rounded-md border-2 group p-0" style={{ borderColor: 'var(--color-header-accent, #0A74FF)' } as React.CSSProperties}>
                  {isCreatePage ? (
                    <a
                      href="/liquidity"
                      className="inline-flex flex-col items-center justify-center gap-0.5"
                      title="Liquidity Management"
                      onClick={handleNavigation('/liquidity')}
                    >
                      <Droplets className="w-4 h-4 text-[var(--color-header-accent,#0A74FF)]" />
                      <span className="hidden lg:inline text-sm">Liquidity</span>
                      <span className="text-[10px] lg:hidden -mt-1">Liquid</span>
                    </a>
                  ) : (
                    <SafeLink
                      to="/liquidity"
                      className="inline-flex flex-col items-center justify-center gap-0.5"
                      title="Liquidity Management"
                    >
                      <Droplets className="w-4 h-4 text-[var(--color-header-accent,#0A74FF)]" />
                      <span className="hidden lg:inline text-sm">Liquidity</span>
                      <span className="text-[10px] lg:hidden -mt-1">Liquid</span>
                    </SafeLink>
                  )}
                </Button>
              </div>
            </nav>
          </div>

          {/* Right Side Controls - Only show in horizontal layouts */}
          {(buttonLayout === 'Top' || buttonLayout === 'Bottom') && (
            <div className="flex items-center gap-2">
              <ThemeToggle />
            <Button
              onClick={handleShare}
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0 hover:bg-accent rounded-md border-2 group relative"
              style={{ borderColor: 'var(--color-header-accent, #0A74FF)' }}
              title="Share MdbApp"
            >
              {/* Spinning Links - one themed to header accent, one to text color */}
              <Link className="w-4 h-4 absolute" style={{
                color: 'var(--color-header-accent, #0A74FF)',
                opacity: '0.6',
                animation: 'spin 3s linear infinite'
              }} />
              <Link className="w-4 h-4 absolute" style={{
                color: 'var(--color-text, #e5e5e5)',
                opacity: '0.4',
                animation: 'spin-reverse 3s linear infinite'
              }} />
            </Button>
          </div>
          )}
        </div>
      </div>
      
      {/* Bottom Blue Line - Only for Bottom layout */}
      {buttonLayout === 'Bottom' && (
        <>
          <div className="w-full h-[1.4px]" style={{ backgroundColor: 'var(--color-header-accent, #0A74FF)', boxShadow: '0 0 8px var(--color-header-accent, rgba(10, 116, 255, 0.5))' }}></div>
          {/* Bottom safe-area spacer so the blue line is visible above system bar in Bottom mode - only on native platforms */}
          {typeof window !== 'undefined' && (window.matchMedia('(display-mode: standalone)').matches || Capacitor.isNativePlatform()) && (
            <div className="w-full bg-background/50" style={{ 
              height: 'env(safe-area-inset-bottom, 20px)',
              minHeight: '20px'
            }} />
          )}
        </>
      )}
    </header>
    <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </>
  );
}